import React, { Suspense, useState } from 'react';
import type { StyleProp, ViewStyle } from 'react-native';

import { AppPressable } from '../components/AppPressable';
import { useLocale } from '../i18n/LocaleContext';
import { NoteGlyph } from './MusicGlyphs';
import { useMusicPlayerOptional } from './MusicPlayerContext';
import { NowPlayingBars } from './NowPlayingBars';

/**
 * Окно плеера грузится по первому нажатию на ноту: вместе с эквалайзером и волной это
 * больше тысячи строк, а в основном бандле веба они вывели его за бюджет размера сборки.
 */
const MusicPlayerModal = React.lazy(() =>
  import('./MusicPlayerModal').then((m) => ({ default: m.MusicPlayerModal })),
);

/**
 * Кнопка плеера в шапке списка чатов (заказ владельца 2026-10-04: «нота наверху рядом с
 * настройками»). Пока музыка играет, нота превращается в пляшущие столбики — из списка чатов
 * видно, что звук идёт от нас, и понятно, куда нажать, чтобы его остановить.
 *
 * <p>Экран плеера открывается отсюда же, поэтому кнопка самодостаточна: в шапку она встаёт
 * одной строкой, и переставить её при переделке шапки — тоже одна строка.
 */
export function MusicHeaderButton({
  tint,
  style,
  iconSize = 22,
}: {
  tint: string;
  style?: StyleProp<ViewStyle>;
  iconSize?: number;
}): React.ReactElement | null {
  const { t } = useLocale();
  const player = useMusicPlayerOptional();
  const [open, setOpen] = useState(false);
  if (!player) {
    return null;
  }
  const playing = player.status === 'playing' || player.status === 'loading';
  return (
    <>
      <AppPressable
        onPress={() => setOpen(true)}
        hitSlop={8}
        style={style}
        accessibilityRole="button"
        accessibilityLabel={t('musicOpenA11y')}>
        {playing ? (
          <NowPlayingBars playing color={tint} size={iconSize - 4} />
        ) : (
          <NoteGlyph size={iconSize} color={tint} />
        )}
      </AppPressable>
      {open ? (
        <Suspense fallback={null}>
          <MusicPlayerModal visible onClose={() => setOpen(false)} />
        </Suspense>
      ) : null}
    </>
  );
}
